import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";

export interface EditorialPageHeaderProps {
  title: ReactNode;
  kicker?: ReactNode;
  metadata?: ReactNode;
  action?: ReactNode;
}

/** Route-level hierarchy only. Feature state and workflow stay with the page. */
export const EditorialPageHeader = ({ title, kicker, metadata, action }: EditorialPageHeaderProps) => (
  <Box
    component="header"
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1fr) auto" },
      columnGap: { xs: 2, md: 4 },
      rowGap: 2,
      alignItems: "end",
      minWidth: 0,
      pb: { xs: 3, sm: 4 },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      {kicker && (
        <Typography
          sx={{
            mb: 1.25,
            color: "secondary.main",
            fontSize: ".65rem",
            fontWeight: 800,
            letterSpacing: ".12em",
            lineHeight: 1.5,
            textTransform: "uppercase",
          }}
        >
          {kicker}
        </Typography>
      )}
      <Typography
        component="h1"
        sx={{
          m: 0,
          maxWidth: "18ch",
          fontSize: { xs: "clamp(2.5rem,13vw,4rem)", sm: "clamp(3.75rem,8vw,6.75rem)" },
          fontWeight: 800,
          letterSpacing: "-.09em",
          lineHeight: 0.84,
          overflowWrap: "anywhere",
          textWrap: "balance",
        }}
      >
        {title}
      </Typography>
      {metadata && (
        <Box
          data-editorial-metadata
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 2,
            color: "text.secondary",
            fontSize: ".68rem",
            lineHeight: 1.55,
          }}
        >
          {metadata}
        </Box>
      )}
    </Box>
    {action && (
      <Box
        data-editorial-action
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          justifyContent: { xs: "flex-start", md: "flex-end" },
          "& :is(button,a)": { minHeight: 44 },
          "& :is(button,a):focus-visible": { outline: "2px solid", outlineColor: "secondary.main", outlineOffset: 3 },
        }}
      >
        {action}
      </Box>
    )}
  </Box>
);
